import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Trash2, Plus, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import RoomTemplateBuilder from './RoomTemplateBuilder';
import { 
  RoomTemplate, 
  WeeklyTemplate, 
  TimeInterval,
  generateDefaultTemplates 
} from '../utils/roomTemplateUtils';

// Use interfaces from roomTemplateUtils

interface DailyOverride {
  date: string; // ISO date string (YYYY-MM-DD)
  is_available: boolean;
  intervals: TimeInterval[];
  notes?: string;
}

interface Room {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
}

interface RoomAvailability {
  id: string;
  room_id: string;
  year: number;
  month: number;
  weekly_template: WeeklyTemplate;
  daily_overrides: DailyOverride[];
  template_name: string;
  room?: Room;
}

interface CalendarDay {
  date: Date;
  day: number;
  is_available: boolean;
  intervals: TimeInterval[];
  notes?: string;
  is_weekend: boolean;
  is_today: boolean;
  is_past: boolean;
  is_override: boolean;
}

interface MonthlyRoomAvailabilityManagerProps {
  roomId: string;
  isAdmin?: boolean;
  onRoomAvailabilityChange?: (roomId: string, availability: RoomAvailability) => void;
}

const MonthlyRoomAvailabilityManager: React.FC<MonthlyRoomAvailabilityManagerProps> = ({
  roomId,
  isAdmin = false,
  onRoomAvailabilityChange
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [roomAvailability, setRoomAvailability] = useState<RoomAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'calendar' | 'monthly-config'>('calendar');
  const [selectedTemplate, setSelectedTemplate] = useState<RoomTemplate | null>(null);
  // Template builder state is handled by the RoomTemplateBuilder component
  const [bulkApplyMonths, setBulkApplyMonths] = useState<{ year: number; month: number }[]>([]);
  const [showBulkApplyModal, setShowBulkApplyModal] = useState(false);
  const [showDailyOverrideModal, setShowDailyOverrideModal] = useState(false);
  const [selectedDayForOverride, setSelectedDayForOverride] = useState<CalendarDay | null>(null);
  const [dailyOverrideData, setDailyOverrideData] = useState<{
    is_available: boolean;
    intervals: TimeInterval[];
    notes?: string;
  }>({
    is_available: false,
    intervals: [{ start_time: '09:00', end_time: '17:00' }],
    notes: ''
  });
  
  // Month-specific configuration state
  const [monthlyConfigurations, setMonthlyConfigurations] = useState<Map<string, RoomAvailability>>(new Map());
  const [monthlyConfigStatus, setMonthlyConfigStatus] = useState<Map<string, boolean>>(new Map());
  const [yearlyStats, setYearlyStats] = useState<{
    configuredMonths: number;
    totalMonths: number;
    templates: Map<string, string>;
  }>({
    configuredMonths: 0,
    totalMonths: 12,
    templates: new Map()
  });

  const year = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const month = useMemo(() => currentDate.getMonth() + 1, [currentDate]);
  
  // Generate month key for current month
  const monthKey = useMemo(() => `${year}-${month.toString().padStart(2, '0')}`, [year, month]);
  


  // Generate default templates using the new system
  const defaultTemplates = generateDefaultTemplates();

  // Initialize with default template
  useEffect(() => {
    if (!selectedTemplate && Object.keys(defaultTemplates).length > 0) {
      setSelectedTemplate(defaultTemplates['business-hours']);
    }
  }, [defaultTemplates, selectedTemplate]);

  const fetchRoomAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/rooms/${roomId}/availability/${year}/${month}`);
      
      // Transform API response to match expected structure
      const transformedData = {
        id: response.data.id || '',
        room_id: String(response.data.room_id), // Convert number to string
        year: response.data.year,
        month: response.data.month,
        weekly_template: response.data.weekly_template,
        daily_overrides: response.data.daily_overrides || [],
        template_name: response.data.template_name || 'Sin Configurar',
        room: response.data.room || { 
          id: String(response.data.room_id), 
          name: response.data.room_name || '',
          description: '',
          is_active: true,
          sort_order: 0
        }
      };
      
      setRoomAvailability(transformedData);
      
      // Store this month's configuration in the monthly configurations map
      setMonthlyConfigurations(prev => {
        const newMap = new Map(prev);
        newMap.set(monthKey, transformedData);
        return newMap;
      });
      
      // Template data is now handled by the template builder
    } catch (error) {
      console.error('Error fetching room availability:', error);
      // Set default template if no data exists
      const defaultAvailability = {
        id: '',
        room_id: roomId,
        year,
        month,
        weekly_template: {
          monday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          tuesday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          wednesday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          thursday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          friday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          saturday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
          sunday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] }
        },
        daily_overrides: [],
        template_name: 'Sin Configurar',
        room: { 
          id: roomId, 
          name: '', 
          description: '',
          is_active: true,
          sort_order: 0
        }
      };
      
      setRoomAvailability(defaultAvailability);
      
      // Store default configuration for this month
      setMonthlyConfigurations(prev => {
        const newMap = new Map(prev);
        newMap.set(monthKey, defaultAvailability);
        return newMap;
      });
    } finally {
      setLoading(false);
    }
  }, [roomId, year, month, monthKey]);

  // Fetch configuration status for all months of the current year
  const fetchYearlyConfigStatus = useCallback(async () => {
    try {
      const response = await api.get(`/admin/rooms/${roomId}/availability/${year}/status`);
      const { monthly_status, monthly_templates, configured_months } = response.data;
      
      // Convert the response to our state format
      const statusMap = new Map<string, boolean>();
      const templateMap = new Map<string, string>();
      
      for (let monthNum = 1; monthNum <= 12; monthNum++) {
        const monthKey = `${year}-${monthNum.toString().padStart(2, '0')}`;
        statusMap.set(monthKey, monthly_status[monthNum] || false);
        if (monthly_templates[monthNum]) {
          templateMap.set(monthKey, monthly_templates[monthNum]);
        }
      }
      
      setMonthlyConfigStatus(statusMap);
      setYearlyStats({
        configuredMonths: configured_months,
        totalMonths: 12,
        templates: templateMap
      });
    } catch (error) {
      console.error('Error fetching yearly config status:', error);
      // Set all months as not configured if there's an error
      const statusMap = new Map<string, boolean>();
      for (let monthNum = 1; monthNum <= 12; monthNum++) {
        const monthKey = `${year}-${monthNum.toString().padStart(2, '0')}`;
        statusMap.set(monthKey, false);
      }
      setMonthlyConfigStatus(statusMap);
      setYearlyStats({
        configuredMonths: 0,
        totalMonths: 12,
        templates: new Map()
      });
    }
  }, [roomId, year]);

  useEffect(() => {
    // Only fetch when component mounts
    fetchRoomAvailability();
    // Also fetch the yearly configuration status
    fetchYearlyConfigStatus();
  }, [fetchRoomAvailability, fetchYearlyConfigStatus]); // Include dependencies

  // Refresh yearly config status when year changes
  useEffect(() => {
    fetchYearlyConfigStatus();
  }, [year, fetchYearlyConfigStatus]);

  // Load configuration when month changes
  useEffect(() => {
    // Check if we already have this month's configuration
    if (monthlyConfigurations.has(monthKey)) {
      const existingConfig = monthlyConfigurations.get(monthKey);
      if (existingConfig) {
        setRoomAvailability(existingConfig);
        return;
      }
    }
    
    // If not, fetch it
    fetchRoomAvailability();
  }, [monthKey, monthlyConfigurations, fetchRoomAvailability]);

  const applyTemplate = async (template: RoomTemplate) => {
    try {
      setLoading(true);
      
      await api.put(`/admin/rooms/${roomId}/availability/${year}/${month}`, {
        weekly_template: template.schedule,
        template_name: template.name
      });
      
      // Update the monthly config status for this month
      setMonthlyConfigStatus(prev => {
        const newMap = new Map(prev);
        newMap.set(monthKey, true);
        return newMap;
      });

      // Update yearly stats
      setYearlyStats(prev => {
        const wasConfigured = prev.templates.has(monthKey);
        const newTemplates = new Map(prev.templates);
        newTemplates.set(monthKey, template.name);
        
        return {
          ...prev,
          configuredMonths: wasConfigured ? prev.configuredMonths : prev.configuredMonths + 1,
          templates: newTemplates
        };
      });
      
      toast.success(`Plantilla "${template.name}" aplicada exitosamente`);
      await fetchRoomAvailability();
      onRoomAvailabilityChange?.(roomId, roomAvailability!);
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error('Error al aplicar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  // Custom schedule application is now handled by the template builder

  const applyTemplateToMonths = async (template: RoomTemplate, months: { year: number; month: number }[]) => {
    try {
      setLoading(true);
      
      for (const monthData of months) {
        await api.put(`/admin/rooms/${roomId}/availability/${monthData.year}/${monthData.month}`, {
          weekly_template: template.schedule,
          template_name: template.name
        });
        
        // Update the monthly config status for this month
        const appliedMonthKey = `${monthData.year}-${monthData.month.toString().padStart(2, '0')}`;
        setMonthlyConfigStatus(prev => {
          const newMap = new Map(prev);
          newMap.set(appliedMonthKey, true);
          return newMap;
        });

        // Update yearly stats for this month
        setYearlyStats(prev => {
          const wasConfigured = prev.templates.has(appliedMonthKey);
          const newTemplates = new Map(prev.templates);
          newTemplates.set(appliedMonthKey, template.name);
          
          return {
            ...prev,
            configuredMonths: wasConfigured ? prev.configuredMonths : prev.configuredMonths + 1,
            templates: newTemplates
          };
        });
      }
      
      toast.success(`Plantilla "${template.name}" aplicada a ${months.length} mes(es)`);
      setShowBulkApplyModal(false);
      setBulkApplyMonths([]);
      await fetchRoomAvailability();
    } catch (error) {
      console.error('Error applying template to months:', error);
      toast.error('Error al aplicar la plantilla a múltiples meses');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template: RoomTemplate) => {
    setSelectedTemplate(template);
    // Apply the template to the current month
    applyTemplate(template);
  };

  const handleTemplateSelectForBulk = (template: RoomTemplate) => {
    setSelectedTemplate(template);
    // Don't apply immediately, just select for bulk operation
  };

  const copyMonthConfiguration = async (sourceMonthKey: string, targetMonthKey: string) => {
    try {
      setLoading(true);
      const sourceConfig = monthlyConfigurations.get(sourceMonthKey);
      
      if (!sourceConfig) {
        toast.error('No hay configuración para copiar del mes seleccionado');
        return;
      }
      
      // Parse the target month key
      const [targetYear, targetMonth] = targetMonthKey.split('-');
      
      await api.put(`/admin/rooms/${roomId}/availability/${targetYear}/${targetMonth}`, {
        weekly_template: sourceConfig.weekly_template,
        daily_overrides: sourceConfig.daily_overrides,
        template_name: `${sourceConfig.template_name} (Copiado)`
      });
      
      toast.success(`Configuración copiada exitosamente de ${sourceMonthKey} a ${targetMonthKey}`);
      
      // Refresh the current month's data
      await fetchRoomAvailability();
    } catch (error) {
      console.error('Error copying month configuration:', error);
      toast.error('Error al copiar la configuración del mes');
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarDays = (): (CalendarDay | null)[] => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const today = new Date();
    
    // Get the day of week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = getDay(start);
    
    // Create array with empty cells for days before the month starts
    const days: (CalendarDay | null)[] = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    const monthDays = eachDayOfInterval({ start, end }).map(date => {
      const dayOfWeek = getDay(date);
      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
      const weeklySchedule = roomAvailability?.weekly_template || defaultTemplates['business-hours'].schedule;
      const daySchedule = weeklySchedule[dayName as keyof WeeklyTemplate];
      
      // Check for daily overrides
      const dateString = format(date, 'yyyy-MM-dd');
      const override = roomAvailability?.daily_overrides.find(o => o.date === dateString);
      
      return {
        date,
        day: date.getDate(),
        is_available: override ? override.is_available : daySchedule.is_available,
        intervals: override ? override.intervals : daySchedule.intervals,
        notes: override?.notes,
        is_weekend: dayOfWeek === 0 || dayOfWeek === 6,
        is_today: isSameDay(date, today),
        is_past: date < today,
        is_override: !!override
      };
    });
    
    days.push(...monthDays);
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Custom schedule functions are now handled by the template builder

  const handleDayClick = (day: CalendarDay) => {
    setSelectedDayForOverride(day);
    
    // Initialize override data with current day settings
    if (day.is_override) {
      // If there's already an override, use its data
      setDailyOverrideData({
        is_available: day.is_available,
        intervals: day.intervals,
        notes: day.notes || ''
      });
    } else {
      // Use the weekly template data for this day
      const dayOfWeek = getDay(day.date);
      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
      const weeklySchedule = roomAvailability?.weekly_template || defaultTemplates['business-hours'].schedule;
      const daySchedule = weeklySchedule[dayName as keyof WeeklyTemplate];
      
      setDailyOverrideData({
        is_available: daySchedule.is_available,
        intervals: daySchedule.intervals,
        notes: ''
      });
    }
    
    setShowDailyOverrideModal(true);
  };

  const saveDailyOverride = async () => {
    if (!selectedDayForOverride || !roomAvailability) return;
    
    try {
      setLoading(true);
      
      // Get current daily overrides
      const currentOverrides = roomAvailability.daily_overrides || [];
      
      // Remove existing override for this day if it exists
      const dateString = format(selectedDayForOverride.date, 'yyyy-MM-dd');
      const filteredOverrides = currentOverrides.filter(o => o.date !== dateString);
      
      // Add new override
      const newOverride = {
        date: dateString,
        is_available: dailyOverrideData.is_available,
        intervals: dailyOverrideData.intervals,
        notes: dailyOverrideData.notes || undefined
      };
      
      const updatedOverrides = [...filteredOverrides, newOverride];
      
      // Update the room availability with new overrides
      await api.put(`/admin/rooms/${roomId}/availability/${year}/${month}`, {
        weekly_template: roomAvailability.weekly_template,
        daily_overrides: updatedOverrides,
        template_name: roomAvailability.template_name
      });
      
      // Refresh the data
      await fetchRoomAvailability();
      
      const formattedDate = format(selectedDayForOverride.date, 'EEEE d \'de\' MMMM, yyyy', { locale: es });
      toast.success(`Configuración del ${formattedDate} actualizada exitosamente`);
      setShowDailyOverrideModal(false);
      setSelectedDayForOverride(null);
    } catch (error) {
      console.error('Error saving daily override:', error);
      toast.error('Error al guardar la configuración del día');
    } finally {
      setLoading(false);
    }
  };

  const removeDailyOverride = async () => {
    if (!selectedDayForOverride || !roomAvailability) return;
    
    try {
      setLoading(true);
      
      // Remove the override for this day
      const dateString = format(selectedDayForOverride.date, 'yyyy-MM-dd');
      const updatedOverrides = roomAvailability.daily_overrides.filter(o => o.date !== dateString);
      
      // Update the room availability
      await api.put(`/admin/rooms/${roomId}/availability/${year}/${month}`, {
        weekly_template: roomAvailability.weekly_template,
        daily_overrides: updatedOverrides,
        template_name: roomAvailability.template_name
      });
      
      // Refresh the data
      await fetchRoomAvailability();
      
      const formattedDate = format(selectedDayForOverride.date, 'EEEE d \'de\' MMMM, yyyy', { locale: es });
      toast.success(`Configuración personalizada del ${formattedDate} eliminada`);
      setShowDailyOverrideModal(false);
      setSelectedDayForOverride(null);
    } catch (error) {
      console.error('Error removing daily override:', error);
      toast.error('Error al eliminar la configuración personalizada');
    } finally {
      setLoading(false);
    }
  };

  const addOverrideTimeInterval = () => {
    setDailyOverrideData(prev => ({
      ...prev,
      intervals: [...prev.intervals, { start_time: '09:00', end_time: '17:00' }]
    }));
  };

  const removeOverrideTimeInterval = (index: number) => {
    setDailyOverrideData(prev => ({
      ...prev,
      intervals: prev.intervals.filter((_, i) => i !== index)
    }));
  };

  const updateOverrideTimeInterval = (index: number, field: 'start_time' | 'end_time', value: string) => {
    setDailyOverrideData(prev => ({
      ...prev,
      intervals: prev.intervals.map((interval, i) => 
        i === index ? { ...interval, [field]: value } : interval
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Gestión de Disponibilidad - {roomAvailability?.room?.name || 'Consultorio'}
            </h2>
            <p className="text-sm text-blue-600">
              Configura horarios y disponibilidad por mes
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveView('calendar')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
            activeView === 'calendar'
              ? 'bg-white text-primary-600 shadow-sm transform scale-105'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <Calendar className="h-4 w-4" />
          <span>Calendario</span>
        </button>

        <button
          onClick={() => setActiveView('monthly-config')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
            activeView === 'monthly-config'
              ? 'bg-white text-primary-600 shadow-sm transform scale-105'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <CalendarDays className="h-4 w-4" />
          <span>Plantillas y Configuración</span>
        </button>
      </div>

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Calendar Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h3 className="text-xl font-semibold text-gray-900 capitalize">
                  {format(currentDate, 'MMMM yyyy', { locale: es })}
                </h3>
                <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full font-medium">
                  {monthKey}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Hoy
                </button>
                <button
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-6">
            <div className="grid grid-cols-7 gap-3">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg">
                  {day}
                </div>
              ))}
              
              {calendarDays.map((day, index) => (
                <div
                  key={day ? day.date.toISOString() : `empty-${index}`}
                  className={`p-3 min-h-[100px] rounded-lg border-2 transition-all duration-200 ${
                    day ? (
                      day.is_today 
                        ? 'bg-primary-50 border-primary-300 shadow-md ring-2 ring-primary-200' 
                        : day.is_past
                        ? 'bg-gray-50 border-gray-200'
                        : day.is_weekend
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                    ) : 'bg-gray-50 border-gray-100'
                  } ${day && !day.is_past ? 'cursor-pointer hover:transform hover:scale-105' : ''}`}
                  onClick={day && !day.is_past ? () => handleDayClick(day) : undefined}
                >
                  {day ? (
                    <>
                      <div className={`text-lg font-bold mb-2 ${
                        day.is_today ? 'text-primary-700' : 
                        day.is_past ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                        {day.day}
                      </div>
                      <div className="space-y-1">
                        {day.is_available ? (
                          <div className="space-y-1">
                            {day.intervals.map((interval, intervalIndex) => (
                              <div key={intervalIndex} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-md font-medium">
                                {interval.start_time} - {interval.end_time}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded-md font-medium">
                            No disponible
                          </div>
                        )}
                        {day.is_override && (
                          <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-md font-medium flex items-center space-x-1">
                            <span>✏️</span>
                            <span>Personalizado</span>
                          </div>
                        )}
                        {!day.is_past && (
                          <div className="text-xs text-gray-500 mt-2 opacity-75">
                            Click para editar
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-400 text-sm">-</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}



      {/* Monthly Configuration View */}
      {activeView === 'monthly-config' && (
        <div className="space-y-8">
          {/* Month Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h3 className="text-xl font-semibold text-gray-900 capitalize">
                  Configuración para {format(currentDate, 'MMMM yyyy', { locale: es })}
                </h3>
                <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full font-medium">
                  {monthKey}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Hoy
                </button>
                <button
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Templates Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Plantillas de Horario</h3>
              <button
                onClick={() => setShowBulkApplyModal(true)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm"
              >
                Aplicar a Múltiples Meses
              </button>
            </div>

            {/* Template Builder */}
            <div className="border border-gray-200 rounded-xl p-6">
              <RoomTemplateBuilder
                onTemplateSelect={handleTemplateSelect}
                initialTemplate={selectedTemplate || undefined}
                applyImmediately={true}
              />
            </div>
          </div>

          {/* Yearly Summary Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Resumen del Año {year}</h3>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{yearlyStats.configuredMonths}</div>
                  <div className="text-sm text-gray-600">de {yearlyStats.totalMonths} meses configurados</div>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {Math.round((yearlyStats.configuredMonths / yearlyStats.totalMonths) * 100)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progreso de configuración</span>
                <span>{yearlyStats.configuredMonths}/{yearlyStats.totalMonths}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(yearlyStats.configuredMonths / yearlyStats.totalMonths) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Monthly Status Grid */}
            <div className="grid grid-cols-6 md:grid-cols-12 gap-2 mb-6">
              {Array.from({ length: 12 }, (_, i) => {
                const monthNum = i + 1;
                const monthKey = `${year}-${monthNum.toString().padStart(2, '0')}`;
                const hasConfig = monthlyConfigStatus.get(monthKey) || false;
                const isCurrentMonth = monthNum === month;
                const templateName = yearlyStats.templates.get(monthKey);
                
                return (
                  <div
                    key={monthNum}
                    className={`relative p-3 text-center rounded-lg border-2 transition-all duration-200 ${
                      isCurrentMonth
                        ? 'bg-primary-100 border-primary-300 shadow-md'
                        : hasConfig
                        ? 'bg-green-100 border-green-300'
                        : 'bg-gray-100 border-gray-300'
                    }`}
                  >
                    <div className={`text-xs font-medium ${
                      isCurrentMonth ? 'text-primary-700' : hasConfig ? 'text-green-700' : 'text-gray-600'
                    }`}>
                      {new Date(year, monthNum - 1).toLocaleDateString('es-AR', { month: 'short' })}
                    </div>
                    {hasConfig && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                    {isCurrentMonth && (
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-primary-500 rounded-full"></div>
                    )}
                    {hasConfig && templateName && (
                      <div className="text-xs text-gray-500 mt-1 truncate" title={templateName}>
                        {templateName.length > 8 ? `${templateName.substring(0, 8)}...` : templateName}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowBulkApplyModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Configurar Múltiples Meses
              </button>
              {yearlyStats.configuredMonths > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      copyMonthConfiguration(e.target.value, monthKey);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Copiar configuración de...</option>
                  {Array.from(monthlyConfigStatus.entries())
                    .filter(([configMonthKey, hasConfig]) => hasConfig && configMonthKey !== monthKey)
                    .map(([configMonthKey]) => {
                      const [configYear, configMonth] = configMonthKey.split('-');
                      const monthName = new Date(parseInt(configYear), parseInt(configMonth) - 1).toLocaleDateString('es-AR', { month: 'long' });
                      const templateName = yearlyStats.templates.get(configMonthKey);
                      return (
                        <option key={configMonthKey} value={configMonthKey}>
                          {monthName} ({templateName})
                        </option>
                      );
                    })}
                </select>
              )}
            </div>
          </div>

          {/* Custom Weekly Configuration Section */}
          {/* Custom schedule configuration is now handled by the template builder above */}
        </div>
      )}

      {/* Bulk Apply Modal */}
      {showBulkApplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Configurar y Aplicar Plantilla a Múltiples Meses
            </h3>
            
            <div className="space-y-6">
              {/* Template Builder */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Configurar Plantilla</h4>
                <RoomTemplateBuilder
                  onTemplateSelect={handleTemplateSelectForBulk}
                  initialTemplate={selectedTemplate || undefined}
                  applyImmediately={false}
                />
              </div>
              
              {/* Month Selection */}
              {selectedTemplate && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Meses para Aplicar Plantilla: "{selectedTemplate.name}"
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <div key={month} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`month-${month}`}
                          checked={bulkApplyMonths.some((m) => m.year === new Date().getFullYear() && m.month === month)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkApplyMonths(prev => [...prev, { year: new Date().getFullYear(), month }]);
                            } else {
                              setBulkApplyMonths(prev => prev.filter((m) => !(m.year === new Date().getFullYear() && m.month === month)));
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`month-${month}`} className="ml-2 block text-sm text-gray-700">
                          {new Date(2024, month - 1).toLocaleDateString('es-AR', { month: 'long' })}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  if (bulkApplyMonths.length === 0) {
                    toast.error('Selecciona al menos un mes para aplicar la plantilla');
                    return;
                  }
                  if (!selectedTemplate) {
                    toast.error('Selecciona una plantilla primero');
                    return;
                  }
                  applyTemplateToMonths(selectedTemplate, bulkApplyMonths);
                }}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
              >
                Aplicar Plantilla a {bulkApplyMonths.length} Mes(es)
              </button>
              <button
                onClick={() => setShowBulkApplyModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Override Modal */}
      {showDailyOverrideModal && selectedDayForOverride && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Configuración del {format(selectedDayForOverride.date, 'EEEE d \'de\' MMMM, yyyy', { locale: es })}
              </h3>
              <button
                onClick={() => setShowDailyOverrideModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Cerrar</span>
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Availability Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Disponible</label>
                <input
                  type="checkbox"
                  checked={dailyOverrideData.is_available}
                  onChange={(e) => setDailyOverrideData(prev => ({ ...prev, is_available: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>

              {/* Time Intervals */}
              {dailyOverrideData.is_available && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Horarios de Disponibilidad</label>
                  
                  {dailyOverrideData.intervals.map((interval, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={interval.start_time}
                        onChange={(e) => updateOverrideTimeInterval(index, 'start_time', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        type="time"
                        value={interval.end_time}
                        onChange={(e) => updateOverrideTimeInterval(index, 'end_time', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => removeOverrideTimeInterval(index)}
                        className="text-red-600 hover:text-red-900 p-1"
                        disabled={dailyOverrideData.intervals.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    onClick={addOverrideTimeInterval}
                    className="text-primary-600 hover:text-primary-900 text-sm flex items-center space-x-1"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Agregar Horario</span>
                  </button>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notas (opcional)</label>
                <textarea
                  value={dailyOverrideData.notes}
                  onChange={(e) => setDailyOverrideData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Agregar notas sobre este día..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={saveDailyOverride}
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Guardar
                </button>
                
                {selectedDayForOverride.is_override && (
                  <button
                    onClick={removeDailyOverride}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
                  >
                    Eliminar Personalización
                  </button>
                )}
                
                <button
                  onClick={() => setShowDailyOverrideModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyRoomAvailabilityManager;
